import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BookOpen,
  CheckCircle,
  Lock,
  MessageSquare,
  Plus,
  RefreshCw,
  Send,
  User,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { communityService } from '../../services/communityService';
import type { CommunityComment, EnrolledSubject } from '../../types/community';
import {
  formatCommentDate,
  getCommentStatusLabel,
  getCommentStatusStyle,
  getReplyDisplayName,
} from '../../utils/communityLabels';

const PAGE_SIZE = 10;
type Tab = 'feed' | 'mine';

function CommentCard({
  comment,
  showStatus,
}: {
  comment: CommunityComment;
  showStatus?: boolean;
}) {
  const contextLabel = [comment.subjectName, comment.courseName, comment.unitName, comment.lessonName]
    .filter(Boolean)
    .join(' · ');

  return (
    <article className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-[#001848]">
            <User size={15} />
          </div>
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-[#001848]">{comment.studentName}</p>
            <p className="text-xs text-slate-400">{formatCommentDate(comment.createdAt)}</p>
          </div>
        </div>
        {showStatus && (
          <span
            className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${getCommentStatusStyle(comment.status)}`}
          >
            {getCommentStatusLabel(comment.status)}
          </span>
        )}
      </div>

      {contextLabel && (
        <p className="mb-2 truncate text-xs text-slate-500">{contextLabel}</p>
      )}

      <p className="whitespace-pre-wrap text-sm leading-relaxed text-slate-700">{comment.text}</p>

      {comment.replies.length > 0 && (
        <div className="mt-3 space-y-2 border-t border-slate-100 pt-3">
          {comment.replies.map((reply) => (
            <div key={reply.id} className="rounded-xl bg-slate-50 p-3 text-sm">
              <div className="mb-1 flex items-center justify-between gap-2">
                <span className="font-semibold text-[#001848]">{getReplyDisplayName(reply)}</span>
                <span className="text-[11px] text-slate-400">{formatCommentDate(reply.createdAt)}</span>
              </div>
              <p className="whitespace-pre-wrap text-slate-600">{reply.text}</p>
            </div>
          ))}
        </div>
      )}
    </article>
  );
}

const PortalForum: React.FC = () => {
  const { currentUser, hasEnrolledCourses, checkEnrolledCourses } = useAuth();
  const isCheckingEnrollment = hasEnrolledCourses === null;
  const [tab, setTab] = useState<Tab>('feed');
  const [subjects, setSubjects] = useState<EnrolledSubject[]>([]);
  const [selectedSubjectId, setSelectedSubjectId] = useState('');
  const [subjectsLoading, setSubjectsLoading] = useState(false);
  const [subjectsError, setSubjectsError] = useState('');
  const [comments, setComments] = useState<CommunityComment[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showCompose, setShowCompose] = useState(false);
  const [newText, setNewText] = useState('');
  const [composeSubjectId, setComposeSubjectId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const fetchSubjects = useCallback(async () => {
    setSubjectsLoading(true);
    setSubjectsError('');
    const result = await communityService.getEnrolledSubjects();
    if (result.success) {
      setSubjects(result.data);
      setSelectedSubjectId((prev) => {
        if (prev && result.data.some((s) => s.id === prev)) return prev;
        return result.data[0]?.id ?? '';
      });
      if (result.data.length === 0) {
        setComments([]);
        setLoading(false);
      }
    } else {
      setSubjects([]);
      setSelectedSubjectId('');
      setSubjectsError(result.error);
      setComments([]);
      setLoading(false);
    }
    setSubjectsLoading(false);
  }, []);

  const fetchComments = useCallback(
    async (targetTab: Tab, targetPage: number, subjectId: string) => {
      if (!subjectId) {
        setComments([]);
        setTotalPages(1);
        setPage(1);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      const params = { subjectId, page: targetPage, pageSize: PAGE_SIZE };
      const result =
        targetTab === 'feed'
          ? await communityService.getPublishedComments(params)
          : await communityService.getMyComments(params);
      if (result.success) {
        setComments(result.data.comments);
        setTotalPages(Math.max(result.data.totalPages, 1));
        setPage(result.data.page);
      } else {
        setComments([]);
        setError(result.error);
      }
      setLoading(false);
    },
    []
  );

  useEffect(() => {
    if (currentUser?.id && hasEnrolledCourses === null) {
      checkEnrolledCourses();
    }
  }, [currentUser?.id, hasEnrolledCourses, checkEnrolledCourses]);

  useEffect(() => {
    if (!hasEnrolledCourses) return;
    fetchSubjects();
  }, [hasEnrolledCourses, fetchSubjects]);

  useEffect(() => {
    if (!hasEnrolledCourses || !selectedSubjectId) return;
    fetchComments(tab, 1, selectedSubjectId);
  }, [tab, selectedSubjectId, fetchComments, hasEnrolledCourses]);

  useEffect(() => {
    if (!showCompose && !showSuccessPopup) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setShowCompose(false);
        setShowSuccessPopup(false);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [showCompose, showSuccessPopup]);

  const openCompose = () => {
    setSubmitError('');
    setComposeSubjectId(selectedSubjectId || subjects[0]?.id || '');
    setShowCompose(true);
  };

  const closeCompose = () => {
    if (submitting) return;
    setShowCompose(false);
    setSubmitError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = newText.trim();
    if (!text) {
      setSubmitError('اكتب سؤالك أولاً');
      return;
    }
    if (!composeSubjectId) {
      setSubmitError('اختر المادة أولاً');
      return;
    }
    setSubmitting(true);
    setSubmitError('');
    const result = await communityService.createComment({
      text,
      subjectId: composeSubjectId,
      courseId: null,
      grade: currentUser?.grade || null,
    });
    setSubmitting(false);
    if (result.success) {
      setNewText('');
      setShowCompose(false);
      setShowSuccessPopup(true);
      if (composeSubjectId !== selectedSubjectId) setSelectedSubjectId(composeSubjectId);
      if (tab === 'mine') fetchComments('mine', 1, composeSubjectId);
    } else {
      setSubmitError(result.error);
    }
  };

  const handlePageChange = (nextPage: number) => {
    if (nextPage < 1 || nextPage > totalPages || !selectedSubjectId) return;
    fetchComments(tab, nextPage, selectedSubjectId);
  };

  if (isCheckingEnrollment) {
    return (
      <div className="mx-auto max-w-2xl space-y-3 p-4 sm:p-6" dir="rtl">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-24 animate-pulse rounded-2xl bg-slate-100" />
        ))}
      </div>
    );
  }

  if (!hasEnrolledCourses) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-8 font-cairo sm:px-6" dir="rtl">
        <h1 className="mb-6 text-xl font-bold text-[#001848] sm:text-2xl">المنتدى</h1>
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-slate-500">
            <Lock className="h-6 w-6" />
          </div>
          <h2 className="mb-2 text-lg font-bold text-[#001848]">المنتدى مقفول حالياً</h2>
          <p className="mb-5 text-sm text-slate-500">اشترك في كورس عشان تقدر تشارك وتقرأ الأسئلة</p>
          <Link
            to="/student-portal/browse"
            className="inline-flex min-h-11 items-center gap-2 rounded-xl bg-[#001848] px-5 text-sm font-semibold text-white"
          >
            <BookOpen size={16} />
            تصفح الكورسات
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-4 py-5 pb-24 font-cairo sm:px-6 sm:py-8 sm:pb-24" dir="rtl">
      {showSuccessPopup && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setShowSuccessPopup(false)}
          role="presentation"
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-6 text-center shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="forum-success-title"
          >
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-orange-50 text-gold-600">
              <CheckCircle className="h-7 w-7" />
            </div>
            <h3 id="forum-success-title" className="mb-1 text-lg font-bold text-[#001848]">
              تمام كده!
            </h3>
            <p className="text-sm text-slate-500">سؤالك اتبعت وهيتراجع قبل النشر</p>
            <button
              type="button"
              onClick={() => setShowSuccessPopup(false)}
              className="mt-5 min-h-11 w-full rounded-xl bg-[#001848] text-sm font-semibold text-white"
            >
              تمام
            </button>
          </div>
        </div>
      )}

      {showCompose && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-4"
          onClick={closeCompose}
          role="presentation"
        >
          <div
            className="w-full max-w-lg rounded-t-2xl bg-white p-4 sm:rounded-2xl sm:p-5"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="forum-compose-title"
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 id="forum-compose-title" className="text-base font-bold text-[#001848]">
                سؤال جديد
              </h2>
              <button
                type="button"
                onClick={closeCompose}
                disabled={submitting}
                className="flex h-10 w-10 items-center justify-center rounded-xl text-slate-400 hover:bg-slate-50"
                aria-label="إغلاق"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              {subjects.length > 0 && (
                <select
                  value={composeSubjectId}
                  onChange={(e) => setComposeSubjectId(e.target.value)}
                  disabled={submitting}
                  className="min-h-11 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 text-sm outline-none focus:border-[#001848] focus:bg-white"
                >
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.name}
                      {subject.gradeName ? ` · ${subject.gradeName}` : ''}
                    </option>
                  ))}
                </select>
              )}
              <textarea
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                rows={4}
                autoFocus
                placeholder="اكتب سؤالك..."
                className="w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-3 py-3 text-sm outline-none focus:border-[#001848] focus:bg-white"
              />
              {submitError && <p className="text-sm text-red-600">{submitError}</p>}
              <div className="flex gap-2">
                <button
                  type="submit"
                  disabled={submitting || !composeSubjectId}
                  className="inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-gold-500 text-sm font-semibold text-white disabled:opacity-50"
                >
                  <Send size={15} />
                  {submitting ? 'جاري الإرسال...' : 'إرسال'}
                </button>
                <button
                  type="button"
                  onClick={closeCompose}
                  disabled={submitting}
                  className="min-h-11 rounded-xl border border-slate-200 px-4 text-sm font-semibold text-slate-600"
                >
                  إلغاء
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-5">
        <h1 className="text-xl font-bold text-[#001848] sm:text-2xl">المنتدى</h1>
        <p className="mt-1 text-sm text-slate-500">أسئلة وتعليقات حسب مادتك</p>
      </div>

      {/* Tabs */}
      <div className="mb-4 flex gap-1 rounded-xl bg-slate-100 p-1">
        <button
          type="button"
          onClick={() => setTab('feed')}
          className={`min-h-10 flex-1 rounded-lg text-sm font-semibold transition ${
            tab === 'feed' ? 'bg-white text-[#001848] shadow-sm' : 'text-slate-500'
          }`}
        >
          المجتمع
        </button>
        <button
          type="button"
          onClick={() => setTab('mine')}
          className={`min-h-10 flex-1 rounded-lg text-sm font-semibold transition ${
            tab === 'mine' ? 'bg-white text-[#001848] shadow-sm' : 'text-slate-500'
          }`}
        >
          أسئلتي
        </button>
      </div>

      {/* Subjects */}
      <div className="mb-4">
        {subjectsLoading ? (
          <div className="flex gap-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-9 w-20 animate-pulse rounded-full bg-slate-100" />
            ))}
          </div>
        ) : subjectsError ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-center text-sm text-red-600">
            {subjectsError}
            <button type="button" onClick={fetchSubjects} className="mt-2 block w-full text-[#001848] underline">
              إعادة المحاولة
            </button>
          </div>
        ) : subjects.length === 0 ? (
          <p className="text-sm text-slate-500">لا توجد مواد مسجّلة</p>
        ) : (
          <div className="flex items-center gap-2">
            <div className="-mx-1 flex flex-1 gap-2 overflow-x-auto px-1 pb-1 [scrollbar-width:none]">
              {subjects.map((subject) => {
                const active = subject.id === selectedSubjectId;
                return (
                  <button
                    key={subject.id}
                    type="button"
                    onClick={() => setSelectedSubjectId(subject.id)}
                    className={`min-h-9 shrink-0 rounded-full px-3.5 text-sm font-medium transition ${
                      active
                        ? 'bg-[#001848] text-white'
                        : 'border border-slate-200 bg-white text-slate-600'
                    }`}
                  >
                    {subject.name}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => selectedSubjectId && fetchComments(tab, page, selectedSubjectId)}
              disabled={!selectedSubjectId}
              className="inline-flex min-h-9 min-w-9 shrink-0 items-center justify-center rounded-full border border-slate-200 text-slate-400 disabled:opacity-40"
              aria-label="تحديث"
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
          </div>
        )}
      </div>

      {/* Feed */}
      {!selectedSubjectId && !subjectsLoading && !subjectsError ? (
        <div className="rounded-2xl border border-slate-200 bg-white py-10 text-center">
          <BookOpen className="mx-auto mb-2 h-7 w-7 text-slate-300" />
          <p className="text-sm text-slate-500">اختر مادة لعرض التعليقات</p>
        </div>
      ) : loading || subjectsLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-24 animate-pulse rounded-2xl bg-slate-100" />
          ))}
        </div>
      ) : error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-center text-sm text-red-600">
          {error}
        </div>
      ) : comments.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white py-10 text-center">
          <MessageSquare className="mx-auto mb-2 h-7 w-7 text-slate-300" />
          <p className="text-sm font-medium text-[#001848]">
            {tab === 'feed' ? 'لا توجد تعليقات بعد' : 'لم تكتب أسئلة بعد'}
          </p>
          <p className="mt-1 text-xs text-slate-400">اضغط سؤال جديد للمشاركة</p>
        </div>
      ) : (
        <div className="space-y-3">
          {comments.map((comment) => (
            <CommentCard key={comment.id} comment={comment} showStatus={tab === 'mine'} />
          ))}
        </div>
      )}

      {totalPages > 1 && !loading && selectedSubjectId && (
        <div className="mt-5 flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 1}
            className="min-h-10 rounded-lg px-3 text-sm text-[#001848] disabled:opacity-40"
          >
            السابق
          </button>
          <span className="text-xs text-slate-500">
            {page} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages}
            className="min-h-10 rounded-lg px-3 text-sm text-[#001848] disabled:opacity-40"
          >
            التالي
          </button>
        </div>
      )}

      <button
        type="button"
        onClick={openCompose}
        disabled={subjects.length === 0}
        className="fixed bottom-5 end-4 z-40 inline-flex min-h-12 items-center gap-2 rounded-full bg-[#001848] px-4 text-sm font-semibold text-white shadow-lg disabled:opacity-50 sm:bottom-6 sm:end-6"
        aria-label="سؤال جديد"
      >
        <Plus className="h-4 w-4" />
        <span className="hidden min-[380px]:inline">سؤال جديد</span>
      </button>
    </div>
  );
};

export default PortalForum;
